import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { formatPhoneNumber } from "../../../utils/formatting/phoneFormatter";

/**
 * Masked Phone Input Component
 * Displays format: (___)-___-____
 * Auto-advances between sections as user types
 */
const PhoneInput = ({
  value = "",
  onChange,
  onBlur,
  name,
  required = false,
  className = "form-input",
  ...rest
}) => {
  const inputRef = useRef(null);
  const [displayValue, setDisplayValue] = useState("(___)-___-____");
  const cursorPosRef = useRef(null);
  const shouldSetCursorRef = useRef(false);
  const isInternalChangeRef = useRef(false);
  const lastNotifiedValueRef = useRef(null); // Track last value we notified to form

  // Extract digits from value
  const getDigits = (val) => {
    return (val || "").replace(/\D/g, "");
  };

  // Format display value with mask (no extra spaces to match cursor positions)
  const formatDisplayValue = (digits) => {
    const mask = "(___)-___-____";
    let formatted = mask;
    let digitIndex = 0;

    for (let i = 0; i < mask.length && digitIndex < digits.length; i++) {
      if (mask[i] === "_") {
        formatted = formatted.substring(0, i) + digits[digitIndex] + formatted.substring(i + 1);
        digitIndex++;
      }
    }

    return formatted;
  };

  // Get cursor position in display value based on digit count
  const getCursorPosition = (digitCount) => {
    // Positions in the mask where digits can be entered: (___)-___-____
    // Index: 01234567890123456
    //        (___)-___-____
    // Digits go at: 1,2,3 (first), 6,7,8 (second), 10,11,12,13 (third)
    // digitCount is the number of digits entered (0-10)
    // positions[digitCount] gives the position for the NEXT digit to be entered
    const positions = [1, 2, 3, 6, 7, 8, 10, 11, 12, 13];
    if (digitCount >= positions.length) {
      return positions[positions.length - 1] + 1;
    }
    return positions[digitCount];
  };

  // Update display when value changes (only from external source, not internal)
  // Value prop contains digits-only (for validation), we format it for display
  useEffect(() => {
    if (!isInternalChangeRef.current) {
      // Value prop is digits-only, extract and format for display
      const digits = getDigits(value);
      const formatted = formatDisplayValue(digits);
      setDisplayValue(formatted);
    }
    isInternalChangeRef.current = false;
  }, [value]);

  // Set cursor position after DOM update
  useLayoutEffect(() => {
    if (shouldSetCursorRef.current && cursorPosRef.current !== null && inputRef.current) {
      inputRef.current.setSelectionRange(cursorPosRef.current, cursorPosRef.current);
      shouldSetCursorRef.current = false;
      cursorPosRef.current = null;
    }
  });

  // Handle change - extract digits and update
  const handleChange = (e) => {
    const inputValue = e.target.value;
    const currentDigits = getDigits(value);
    const newDigits = getDigits(inputValue);
    
    // Limit to 10 digits
    const limitedDigits = newDigits.slice(0, 10);
    
    // Only process if digit count increased
    if (limitedDigits.length > currentDigits.length) {
      const previousLength = currentDigits.length;
      const newLength = limitedDigits.length;
      
      // Update display immediately
      const formattedDisplay = formatDisplayValue(limitedDigits);
      setDisplayValue(formattedDisplay);
      
      // Mark as internal change to prevent useEffect from running
      isInternalChangeRef.current = true;
      
      // Notify parent with digits-only value for storage (validation expects 10 digits)
      // The parent will format it for display, but we store digits-only for validation
      if (onChange) {
        onChange({
          target: {
            value: limitedDigits, // Send digits-only for validation
            name: name,
          },
        });
        lastNotifiedValueRef.current = limitedDigits;
      }

      // ALWAYS set cursor position to prevent browser from moving it to end
      // Auto-advance when sections complete, otherwise move to next digit position
      if (newLength === 3 && previousLength === 2) {
        // Section 1 complete (3rd digit just entered), jump to section 2 (position 6 - first digit of section 2)
        cursorPosRef.current = 6;
      } else if (newLength === 6 && previousLength === 5) {
        // Section 2 complete (6th digit just entered), jump to section 3 (position 10 - first digit of section 3)
        cursorPosRef.current = 10;
      } else {
        // Normal typing - move to next digit position
        // getCursorPosition(newLength) returns the position for the NEXT digit to be entered
        cursorPosRef.current = getCursorPosition(newLength);
      }
      shouldSetCursorRef.current = true;
    }
  };

  // Handle key down - block non-digits
  const handleKeyDown = (e) => {
    const digits = getDigits(value);
    
    // Handle backspace
    if (e.key === "Backspace") {
      e.preventDefault();
      if (digits.length > 0) {
        const newDigits = digits.slice(0, -1);
        const formattedDisplay = formatDisplayValue(newDigits);
        setDisplayValue(formattedDisplay);
        isInternalChangeRef.current = true;
        cursorPosRef.current = getCursorPosition(newDigits.length);
        shouldSetCursorRef.current = true;
        // Send digits-only for validation
        if (onChange) {
          onChange({
            target: {
              value: newDigits, // Send digits-only
              name: name,
            },
          });
        }
      }
      return;
    }
    
    // Block non-digit characters
    const specialKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Enter"];
    if (e.key.length === 1 && !/[0-9]/.test(e.key) && !specialKeys.includes(e.key)) {
      e.preventDefault();
      return;
    }
    
    // Block if we already have 10 digits
    if (digits.length >= 10 && /[0-9]/.test(e.key)) {
      e.preventDefault();
      return;
    }
  };

  // Handle paste
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = (e.clipboardData || window.clipboardData).getData("text");
    const digits = getDigits(pastedText).slice(0, 10);
    const formattedDisplay = formatDisplayValue(digits);
    setDisplayValue(formattedDisplay);
    isInternalChangeRef.current = true;
    cursorPosRef.current = getCursorPosition(digits.length);
    shouldSetCursorRef.current = true;
    // Send digits-only for validation
    if (onChange) {
      onChange({
        target: {
          value: digits, // Send digits-only
          name: name,
        },
      });
    }
  };

  // Handle focus - set cursor to next empty position
  const handleFocus = (e) => {
    const digits = getDigits(value);
    const nextPos = getCursorPosition(digits.length);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(nextPos, nextPos);
      }
    }, 0);
  };

  // Handle click - allow user to position cursor manually
  const handleClick = (e) => {
    // Let the browser handle cursor positioning on click
    // We'll only adjust if they click on a separator
    const clickPos = e.target.selectionStart;
    const digitPositions = [1, 2, 3, 6, 7, 8, 10, 11, 12, 13];
    
    if (!digitPositions.includes(clickPos)) {
      // Clicked on separator, move to next digit position
      const digits = getDigits(value);
      const nextPos = getCursorPosition(digits.length);
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.setSelectionRange(nextPos, nextPos);
        }
      }, 0);
    }
  };

  return (
    <input
      ref={inputRef}
      type="text"
      name={name}
      className={className}
      value={displayValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      onFocus={handleFocus}
      onClick={handleClick}
      onBlur={onBlur}
      required={required}
      inputMode="numeric"
      style={{
        fontFamily: "monospace",
        letterSpacing: "0.5px",
      }}
      {...rest}
    />
  );
};

export default PhoneInput;
